import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import FYSelect from "@/components/FYSelect";
import { loadFinancialYears, fyLabel } from "@/lib/fy";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SkillMatrix({ workforceType = null }) {
  const [cmap, setCmap] = useState([]);
  const [role, setRole] = useState("");
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [rolesInScope, setRolesInScope] = useState(null); // set of role names, or null while loading
  const [fy, setFy] = useState(null);

  useEffect(() => {
    api.get("/competency-map").then((r) => setCmap(r.data));
    loadFinancialYears().then((d) => setFy((prev) => prev ?? d.current_fy));
  }, []);

  // For a workforce-scoped page, only show roles that have at least one
  // active employee of that workforce_type.
  useEffect(() => {
    if (!workforceType) { setRolesInScope(null); return; }
    const params = { workforce_type: workforceType, active_only: true };
    api.get("/employees", { params }).then((r) => {
      const roles = new Set();
      for (const e of r.data) (e.roles || []).forEach((rn) => roles.add(rn));
      setRolesInScope(roles);
    });
  }, [workforceType]);

  const scopedCmap = workforceType && rolesInScope ? cmap.filter((c) => rolesInScope.has(c.role)) : cmap;

  useEffect(() => {
    if (!role || fy === null) { setData(null); return; }
    const params = { fy, ...(workforceType ? { workforce_type: workforceType } : {}) };
    api.get(`/matrix/role/${encodeURIComponent(role)}`, { params }).then((r) => setData(r.data));
  }, [role, workforceType, fy]);

  const exportExcel = async () => {
    if (!role) return;
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (workforceType) params.set("workforce_type", workforceType);
      if (fy !== null) params.set("fy", String(fy));
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await fetch(`${API}/matrix/role/${encodeURIComponent(role)}/excel${qs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `SkillMatrix_${role.replace(/\s+/g, "_")}.xlsx`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded successfully");
    } catch (e) {
      toast.error("Export failed — please try again");
    } finally {
      setDownloading(false);
    }
  };

  const trackLabel = workforceType === "worker" ? "Operators" : workforceType === "staff" ? "Staff" : null;
  const title = workforceType === "staff" ? "Competency Matrix" : workforceType === "worker" ? "Skill Matrix" : "Skill Matrix";
  const eyebrow = workforceType === "staff" ? "Competency · Staff" : workforceType === "worker" ? "Skill · Operators" : "Role-wise Skill Matrix";
  const subtitle = trackLabel
    ? `Select a role to view the competency status of every ${trackLabel === "Staff" ? "staff member" : "operator"} performing that role. Showing results for ${fyLabel(fy)}.`
    : `Select a role to view the competency status of every employee performing that role. Showing results for ${fyLabel(fy)}.`;

  return (
    <div data-testid="skill-matrix-page">
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        subtitle={subtitle}
        breadcrumbs={[{ label: "Home", to: "/" }, { label: title }]}
        action={
          <div className="flex items-end gap-2">
            <FYSelect value={fy} onChange={setFy} testid="skill-matrix-fy-select" />
            <select
              data-testid="role-selector"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm bg-white min-w-[220px]"
            >
              <option value="">Select a role…</option>
              {scopedCmap.map((c) => <option key={c.role} value={c.role}>{c.role}</option>)}
            </select>
            <button
              type="button"
              onClick={exportExcel}
              disabled={downloading || !data}
              data-testid="role-export-excel"
              className="inline-flex items-center gap-1.5 bg-[#0D0D0D] hover:bg-neutral-800 text-white text-sm rounded-sm px-3 py-2 disabled:opacity-50"
            >
              {downloading ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
              {downloading ? "Exporting…" : "Export Excel"}
            </button>
          </div>
        }
      />
      <div className="px-4 md:px-10 py-8">
        {!role && <div className="bc-card p-10 text-sm text-[#666666] text-center">Choose a role above to load the matrix.</div>}
        {role && !data && <div className="bc-card p-10 text-sm text-[#666666]">Loading…</div>}
        {data && (
          <div className="bc-card overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#F9F9F9] border-b-2 border-[#0D0D0D]">
                  <th className="text-left px-3 py-2.5 bc-label">Employee</th>
                  <th className="text-left px-3 py-2.5 bc-label">Dept</th>
                  {data.required_skills.map((s) => (
                    <th key={s.code} className="text-center px-3 py-2.5">
                      <div className="bc-label">{s.name}</div>
                      <div className="text-[10px] text-[#666666] font-mono normal-case mt-0.5">req {s.required_level}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.employees.length === 0 && (
                  <tr><td colSpan={data.required_skills.length + 2} className="px-5 py-8 text-center text-[#666666]">No employees with this role.</td></tr>
                )}
                {data.employees.map((emp) => (
                  <tr key={emp.employee_id} className="border-b border-[#E5E5E5]">
                    <td className="px-3 py-2 font-medium">{emp.name}</td>
                    <td className="px-3 py-2 text-[#666666] text-xs">{emp.department}</td>
                    {data.required_skills.map((s) => {
                      const c = emp.skills[s.code];
                      const cls = `cell-${c.status.replace("_", "-")}`;
                      return (
                        <td key={s.code} className={`text-center px-3 py-2 font-mono text-xs ${cls}`}>
                          {c.achieved || "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
